/**
 * SQL 튜닝 개선안 작성 가이드 — 공용 문서 하나(백엔드 assistant/sqlTuning/writingGuide.js). 모두 읽고, 관리자만 고친다.
 * AI 단계(진단·개선안·해석·최종안)의 프롬프트에 늘 실린다 — 화면은 무엇이 실리는지를 사람이 보게 한다(가이드라인: AI 와 사용자가 같은 기준을 본다).
 */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { FiBookOpen, FiSave, FiRotateCcw } from 'react-icons/fi';
import AppModal from '../common/AppModal';
import { useConfirm } from '../ConfirmDialog';
import { formatApiError } from '../../utils/formatters';
import { storedTime } from '../../utils/storedTime';
import * as api from '../../services/sqlTuning';

function WritingGuideModal({ open, onClose }) {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const [data, setData] = useState(null);
  const [text, setText] = useState('');
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setEditing(false);
    api.getWritingGuide().then((r) => { setData(r); setText(r.guide.body); }).catch(err => toast.error(formatApiError(err, t)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const save = async (body) => {
    setBusy(true);
    try {
      const r = await api.saveWritingGuide(body);
      setData(d => ({ ...d, guide: r.guide }));
      setText(r.guide.body);
      setEditing(false);
      toast.success(t('common:stn.wg.saved'));
    } catch (err) { toast.error(formatApiError(err, t)); } finally { setBusy(false); }
  };
  const reset = async () => {
    const ok = await confirm({ title: t('common:stn.wg.resetTitle'), message: t('common:stn.wg.resetBody'), confirmText: t('common:stn.wg.reset'), variant: 'danger' });
    if (ok) save('');
  };

  const g = data && data.guide;
  return (
    <AppModal open={open} onClose={onClose} title={t('common:stn.wg.title')} icon={<FiBookOpen size={14} />} className="app-modal-default stn-wg-modal">
      <div className="stn-wg-body">
        <div className="stn-row stn-between">
          <span className="stn-note">{t('common:stn.wg.hint')}</span>
          <span className="stn-note">
            {g ? (g.isDefault ? t('common:stn.wg.default') : t('common:stn.wg.rev', { rev: g.rev, by: g.updatedBy || '-', at: storedTime(g.updatedAt, 'min') || '-' })) : ''}
          </span>
        </div>
        {editing
          ? <textarea className="stn-sql stn-wg-text" value={text} onChange={e => setText(e.target.value)} spellCheck={false} />
          : <pre className="stn-pre stn-wg-text">{g ? g.body : t('common:stn.loading')}</pre>}
        <div className="stn-actions">
          {data && data.canEdit && !editing && <button type="button" className="app-btn" onClick={() => setEditing(true)}>{t('common:stn.wg.edit')}</button>}
          {data && data.canEdit && editing && (
            <>
              <button type="button" className="app-btn" onClick={reset} disabled={busy}><FiRotateCcw size={12} /> {t('common:stn.wg.reset')}</button>
              <button type="button" className="app-btn" onClick={() => { setText(g.body); setEditing(false); }} disabled={busy}>{t('common:ui.cancel')}</button>
              <button type="button" className="app-btn app-btn-primary" onClick={() => save(text)} disabled={busy}><FiSave size={12} /> {t('common:stn.save')}</button>
            </>
          )}
          {!editing && <button type="button" className="app-btn" onClick={onClose}>{t('common:ui.close')}</button>}
        </div>
      </div>
    </AppModal>
  );
}

WritingGuideModal.propTypes = { open: PropTypes.bool, onClose: PropTypes.func.isRequired };

export default WritingGuideModal;
